<template>
  <div
    ref="root"
    class="relative w-full text-left"
    :data-testid="testId"
    @mousedown="pointerDownInside = true"
    @focusout="onFocusOut"
  >
    <label class="text-xs font-bold text-gray-600" :for="inputId">{{ label }}</label>
    <div
      class="mt-1 flex h-[44px] items-center gap-2 rounded border px-3"
      :class="
        disabled
          ? 'cursor-not-allowed bg-gray-50 border-gray-200'
          : 'bg-white border-gray-300 focus-within:border-primary20'
      "
    >
      <input
        :id="inputId"
        ref="inputEl"
        role="combobox"
        autocomplete="off"
        class="min-w-0 flex-1 bg-transparent text-sm outline-none"
        :class="!isOpen && !displayValue ? 'italic text-gray-400' : 'text-gray-900'"
        :aria-expanded="isOpen"
        :aria-controls="`${inputId}-list`"
        :aria-activedescendant="activeOptionId"
        :placeholder="isOpen ? placeholder : displayValue || emptyText"
        :value="isOpen && typed ? search : displayValue"
        :disabled="disabled"
        :data-testid="`${testId}-input`"
        @focus="open"
        @click="open"
        @mouseup="onMouseUp"
        @input="onInput"
        @keydown.esc="close"
        @keydown.down.prevent="moveActive(1)"
        @keydown.up.prevent="moveActive(-1)"
        @keydown.enter="onEnter"
      />
      <span
        v-if="!isOpen && displayBadge"
        class="rounded-full bg-primary5 px-2 text-[10px] font-semibold text-primary30"
        :data-testid="`${testId}-badge`"
        >{{ displayBadge }}</span
      >
      <button
        v-if="!isOpen && clearable && displayValue && !disabled"
        type="button"
        class="text-gray-400 hover:text-gray-700"
        aria-label="Limpar"
        :data-testid="`${testId}-clear-button`"
        @click="emit('clear')"
      >
        ✕
      </button>
    </div>
    <div
      v-if="isOpen"
      :id="`${inputId}-list`"
      role="listbox"
      class="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
    >
      <template v-for="section in sections" :key="section.key">
        <p
          v-if="section.title"
          class="px-3 pb-1 pt-2 text-[11px] font-bold uppercase text-gray-500"
        >
          {{ section.title }}
        </p>
        <button
          v-for="option in section.options"
          :id="optionDomId(option)"
          :key="option.key"
          type="button"
          role="option"
          class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
          :class="[optionClass(option), { 'bg-gray-100': isActive(option) }]"
          :aria-selected="isActive(option)"
          :disabled="option.disabled"
          :data-testid="option.testId"
          @mousedown.prevent
          @click="choose(option)"
        >
          <span class="flex-1">{{ option.label }}</span>
          <span
            v-if="option.badge"
            class="rounded-full bg-gray-100 px-2 text-[10px] text-gray-600"
            >{{ option.badge }}</span
          >
          <span v-if="option.hint" class="text-[11px] text-gray-400">{{ option.hint }}</span>
        </button>
      </template>
      <p v-if="!hasOptions" class="px-3 py-2 text-sm text-gray-500">{{ noResultsText }}</p>
      <p
        v-if="footerNote"
        class="border-t border-gray-100 px-3 py-2 text-xs text-gray-500"
        :data-testid="`${testId}-footer-note`"
      >
        {{ footerNote }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { CatalogOption, CatalogSection } from './catalogTypes'

const props = withDefaults(
  defineProps<{
    label: string
    displayValue: string
    displayBadge?: string | null
    emptyText: string
    placeholder?: string
    sections: CatalogSection[]
    search: string
    disabled?: boolean
    clearable?: boolean
    noResultsText?: string
    footerNote?: string | null
    testId: string
  }>(),
  {
    displayBadge: null,
    placeholder: 'Buscar',
    disabled: false,
    clearable: true,
    noResultsText: 'Nada encontrado.',
    footerNote: null
  }
)

const emit = defineEmits<{
  'update:search': [value: string]
  select: [option: CatalogOption]
  clear: []
  open: []
}>()

const root = ref<HTMLElement | null>(null)
const inputEl = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
/** Só depois de digitar o input mostra a busca; até lá mostra o valor atual selecionado. */
const typed = ref(false)
/** mousedown dentro do componente (ex.: barra de rolagem da lista) antes de um focusout. */
const pointerDownInside = ref(false)
let selectOnMouseUp = false
const activeKey = ref<string | null>(null)
const inputId = `${props.testId}-${Math.random().toString(36).slice(2, 8)}`
const hasOptions = computed(() => props.sections.some((section) => section.options.length > 0))
const navigableOptions = computed(() =>
  props.sections.flatMap((section) => section.options).filter((option) => !option.disabled)
)
const activeOption = computed(
  () => navigableOptions.value.find((option) => option.key === activeKey.value) ?? null
)
const activeOptionId = computed(() =>
  isOpen.value && activeOption.value ? optionDomId(activeOption.value) : undefined
)

function optionDomId(option: CatalogOption): string {
  return `${inputId}-opt-${option.key}`
}

function isActive(option: CatalogOption): boolean {
  return isOpen.value && activeKey.value === option.key
}

function open(): void {
  if (props.disabled || isOpen.value) return
  isOpen.value = true
  typed.value = false
  activeKey.value = null
  selectOnMouseUp = true
  emit('update:search', '')
  emit('open')
  // Mantém o valor atual visível e selecionado: digitar substitui.
  void nextTick(() => inputEl.value?.select())
}

function close(): void {
  isOpen.value = false
  typed.value = false
  activeKey.value = null
}

/** No Chrome/Safari o mouseup do clique que deu foco desfaz a seleção feita no focus. */
function onMouseUp(event: MouseEvent): void {
  if (!selectOnMouseUp) return
  selectOnMouseUp = false
  event.preventDefault()
}

function onInput(event: Event): void {
  // Reabre ao digitar depois de escolher/Esc com o foco ainda no input.
  if (!isOpen.value) open()
  typed.value = true
  selectOnMouseUp = false
  emit('update:search', (event.target as HTMLInputElement).value)
}

function moveActive(step: 1 | -1): void {
  if (!isOpen.value) {
    open()
    return
  }
  const options = navigableOptions.value
  if (!options.length) return
  const current = options.findIndex((option) => option.key === activeKey.value)
  const next =
    current === -1
      ? step === 1
        ? 0
        : options.length - 1
      : (current + step + options.length) % options.length
  activeKey.value = options[next].key
  void nextTick(() => {
    const el = document.getElementById(optionDomId(options[next]))
    el?.scrollIntoView?.({ block: 'nearest' })
  })
}

function onEnter(event: KeyboardEvent): void {
  if (!isOpen.value || !activeOption.value) return
  event.preventDefault()
  choose(activeOption.value)
}

watch(
  () => props.search,
  () => (activeKey.value = null)
)

/**
 * Fecha ao sair do componente por teclado (Tab). Cliques internos mantêm o foco; um
 * focusout sem relatedTarget logo após mousedown interno (barra de rolagem) também não fecha.
 */
function onFocusOut(event: FocusEvent): void {
  const next = event.relatedTarget as Node | null
  const pointerInside = pointerDownInside.value
  pointerDownInside.value = false
  if (next && root.value?.contains(next)) return
  if (!next && pointerInside) return
  close()
}

function choose(option: CatalogOption): void {
  if (option.disabled) return
  emit('select', option)
  close()
}

function optionClass(option: CatalogOption): string {
  if (option.kind === 'action') return 'font-semibold text-primary30'
  if (option.kind === 'clear') return 'italic text-gray-500'
  return 'text-gray-800'
}

function onDocumentMouseDown(event: MouseEvent): void {
  if (root.value && !root.value.contains(event.target as Node)) close()
}

function onDocumentMouseUp(): void {
  pointerDownInside.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', onDocumentMouseDown)
  document.addEventListener('mouseup', onDocumentMouseUp)
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentMouseDown)
  document.removeEventListener('mouseup', onDocumentMouseUp)
})
</script>
